import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntAPI from '../../utils/AntAPI';
import AntAuth from '../../utils/AntAuth';
import AntCondition from '../../utils/AntCondition';
import { AntSession } from '../../utils/AntContext';
import AntFileViewer from '../AntFileViewer';
import { sidebarRelativeTime } from '../AntRightSidebar/panels/SidebarStandard';
import { activeMentionQuery, mentionKey, mentionLabel, mentionSpans, mentionTarget, storedMentionSpans } from './mention';
import './index.scss';

// One comment thread for every web host (voucher left sidebar, floating task/record comments).
// Feature set follows ante-mobile-user AntVoucherDiscussionSheet: newest-first paging by created_at,
// reply, like, attachments, @mentions (people, teams, departments, everyone), live refresh,
// visible errors with retry, and Comments/Attachments permissions.

var PAGE_SIZE = 30;

var TEXT = {
    empty: { vi: 'Chưa có bình luận', en: 'No comments yet', fr: 'Aucun commentaire', es: 'Sin comentarios', id: 'Belum ada komentar', ja: 'コメントなし', zh: '暂无评论', ko: '아직 댓글이 없습니다', hi: 'अभी कोई टिप्पणी नहीं', ar: 'لا توجد تعليقات بعد' },
    noVoucher: { vi: 'Mở chứng từ để xem bình luận', en: 'Open a document to see comments', fr: 'Commentaires : ouvrez un document', es: 'Abra un documento para ver los comentarios', id: 'Buka dokumen untuk melihat komentar', ja: '伝票を開くとコメントが表示されます', zh: '打开单据以查看评论', ko: '문서를 열면 댓글을 볼 수 있습니다', hi: 'टिप्पणियाँ देखने के लिए दस्तावेज़ खोलें', ar: 'افتح مستندًا لعرض التعليقات' },
    placeholder: { vi: 'Viết bình luận, gõ @ để nhắc tên', en: 'Write a comment, type @ to mention', fr: 'Écrivez un commentaire, tapez @ pour mentionner', es: 'Escriba un comentario, use @ para mencionar', id: 'Tulis komentar, ketik @ untuk menyebut', ja: 'コメントを入力（@でメンション）', zh: '写评论，输入 @ 提及', ko: '댓글 작성, @로 멘션', hi: 'टिप्पणी लिखें, उल्लेख के लिए @ लिखें', ar: 'اكتب تعليقًا، واكتب @ للإشارة' },
    loading: { vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' },
    more: { vi: 'Xem bình luận cũ hơn', en: 'Show older comments', fr: 'Voir les plus anciens', es: 'Ver comentarios anteriores', id: 'Lihat komentar lebih lama', ja: '以前のコメントを表示', zh: '查看更早的评论', ko: '이전 댓글 보기', hi: 'पुरानी टिप्पणियाँ देखें', ar: 'عرض التعليقات الأقدم' },
    reply: { vi: 'Trả lời', en: 'Reply', fr: 'Répondre', es: 'Responder', id: 'Balas', ja: '返信', zh: '回复', ko: '답글', hi: 'जवाब दें', ar: 'رد' },
    replyTo: { vi: 'Trả lời', en: 'Replying to', fr: 'Réponse à', es: 'Respondiendo a', id: 'Membalas', ja: '返信先', zh: '回复', ko: '답글 대상', hi: 'जवाब', ar: 'الرد على' },
    like: { vi: 'Thích', en: 'Like', fr: "J'aime", es: 'Me gusta', id: 'Suka', ja: 'いいね', zh: '点赞', ko: '좋아요', hi: 'पसंद', ar: 'إعجاب' },
    attach: { vi: 'Đính kèm tệp', en: 'Attach files', fr: 'Joindre fichier', es: 'Adjuntar archivos', id: 'Lampirkan berkas', ja: 'ファイルを添付', zh: '添加附件', ko: '파일 첨부', hi: 'फ़ाइलें संलग्न करें', ar: 'إرفاق ملفات' },
    emoji: { vi: 'Biểu tượng cảm xúc', en: 'Emoji', fr: 'Émoji', es: 'Emoji', id: 'Emoji', ja: '絵文字', zh: '表情', ko: '이모지', hi: 'इमोजी', ar: 'رموز تعبيرية' },
    mention: { vi: 'Nhắc tên', en: 'Mention', fr: 'Mentionner', es: 'Mencionar', id: 'Sebut', ja: 'メンション', zh: '提及', ko: '멘션', hi: 'उल्लेख करें', ar: 'إشارة' },
    send: { vi: 'Gửi (Enter)', en: 'Send (Enter)', fr: 'Envoyer (Entrée)', es: 'Enviar (Intro)', id: 'Kirim (Enter)', ja: '送信（Enter）', zh: '发送（Enter）', ko: '보내기 (Enter)', hi: 'भेजें (Enter)', ar: 'إرسال (Enter)' },
    remove: { vi: 'Bỏ', en: 'Remove', fr: 'Retirer', es: 'Quitar', id: 'Hapus', ja: '削除', zh: '移除', ko: '제거', hi: 'हटाएँ', ar: 'إزالة' },
    deleted: { vi: 'Tệp không còn khả dụng', en: 'File unavailable', fr: 'Fichier indisponible', es: 'Archivo no disponible', id: 'Berkas tidak tersedia', ja: 'ファイルを利用できません', zh: '文件不可用', ko: '파일을 사용할 수 없습니다', hi: 'फ़ाइल उपलब्ध नहीं है', ar: 'الملف غير متاح' },
    loadFailed: { vi: 'Không tải được bình luận', en: 'Could not load comments', fr: 'Commentaires indisponibles', es: 'No se cargaron comentarios', id: 'Komentar tidak dapat dimuat', ja: 'コメントを読み込めません', zh: '无法加载评论', ko: '댓글을 불러올 수 없습니다', hi: 'टिप्पणियाँ लोड नहीं हो सकीं', ar: 'تعذر تحميل التعليقات' },
    sendFailed: { vi: 'Không gửi được bình luận', en: 'Could not send comment', fr: "Impossible d'envoyer le commentaire", es: 'No se pudo enviar el comentario', id: 'Komentar tidak dapat dikirim', ja: 'コメントを送信できません', zh: '无法发送评论', ko: '댓글을 보낼 수 없습니다', hi: 'टिप्पणी नहीं भेजी जा सकी', ar: 'تعذر إرسال التعليق' },
    retry: { vi: 'Thử lại', en: 'Retry', fr: 'Réessayer', es: 'Reintentar', id: 'Coba lagi', ja: '再試行', zh: '重试', ko: '다시 시도', hi: 'फिर करें', ar: 'إعادة' },
    readOnly: { vi: 'Bạn chỉ có quyền xem bình luận', en: 'You can only view comments', fr: 'Commentaires en lecture seule', es: 'Solo puede ver los comentarios', id: 'Anda hanya dapat melihat komentar', ja: 'コメントの閲覧のみ可能です', zh: '您只能查看评论', ko: '댓글을 볼 수만 있습니다', hi: 'आप केवल टिप्पणियाँ देख सकते हैं', ar: 'يمكنك عرض التعليقات فقط' }
};

var T = function (key) {
    return AntLocalization.translateText(TEXT[key]);
};

var EMOJI_SHORTCUTS = [
    [':))', '😂'], [':-)', '😊'], [':)', '🙂'], [':(', '😞'], [';)', '😉'], [':D', '😄'], [':-D', '😄'], ['XD', '😆'], ['xD', '😆'],
    [':P', '😛'], [':p', '😛'], [':O', '😮'], [':o', '😮'], ['<3', '❤️'], [':*', '😘'], ['B)', '😎'], [':|', '😐'], ['>:(', '😠'],
    [":'(", '😢'], ['^_^', '😊'], ['-_-', '😑'], ['T_T', '😭'], [':fire:', '🔥'], [':heart:', '❤️'], [':thumbsup:', '👍'],
    [':check:', '✅'], [':x:', '❌'], [':tada:', '🎉']
];

function convertEmojiShortcuts(text) {
    var result = text || '';
    EMOJI_SHORTCUTS.forEach(function (pair) {
        while (result.indexOf(pair[0]) >= 0) result = result.replace(pair[0], pair[1]);
    });
    return result;
}

var EMOJI_CATEGORIES = [
    { label: { vi: 'Mặt cười', en: 'Smileys', fr: 'Visages', es: 'Caras', id: 'Wajah', ja: '顔', zh: '表情', ko: '표정', hi: 'चेहरे', ar: 'وجوه' },
        emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '😍', '🥰', '😘', '😋', '😛', '😜', '🤪', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😫', '😩', '🥺', '😤', '😠', '😡'] },
    { label: { vi: 'Cử chỉ', en: 'Gestures', fr: 'Gestes', es: 'Gestos', id: 'Gestur', ja: 'ジェスチャー', zh: '手势', ko: '제스처', hi: 'इशारे', ar: 'إيماءات' },
        emojis: ['👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '✌️', '🤞', '🤟', '👌', '👋', '✋', '👆', '👇', '👈', '👉', '☝️'] },
    { label: { vi: 'Biểu tượng', en: 'Symbols', fr: 'Symboles', es: 'Símbolos', id: 'Simbol', ja: '記号', zh: '符号', ko: '기호', hi: 'प्रतीक', ar: 'رموز' },
        emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '💯', '💥', '⭐', '🌟', '✨', '🔥', '💡', '🎉', '✅', '❌', '⚠️', '💬', '📎', '📌'] }
];

var ICON = {
    reply: 'M3 10h10a5 5 0 015 5v6M3 10l6-6M3 10l6 6',
    heart: 'M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 000-7.8z',
    clip: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
    close: 'M6 18L18 6M6 6l12 12',
    send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
    at: 'M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.2a8.9 8.9 0 01-4.5 1.2',
    smile: 'M12 22a10 10 0 100-20 10 10 0 000 20zM8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01',
    file: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6'
};

function Icon(props) {
    return (
        <svg width={props.size || 16} height={props.size || 16} viewBox="0 0 24 24" fill={props.fill || 'none'} stroke="currentColor" strokeWidth={props.stroke || 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={ICON[props.name]} />
        </svg>
    );
}

function initialOf(name) {
    var parts = String(name || '?').trim().split(/\s+/);
    return parts[parts.length - 1].charAt(0).toUpperCase() || '?';
}

function Avatar(props) {
    if (props.src) return <img className="act-avatar" src={props.src} alt="" />;
    return <span className="act-avatar">{initialOf(props.name)}</span>;
}

function likedCodesOf(comment) {
    if (Array.isArray(comment.liked_user_codes)) return comment.liked_user_codes;
    try {
        return JSON.parse(comment.liked_user_codes || '[]') || [];
    } catch (e) {
        return [];
    }
}

function displayNameOf(comment) {
    var profile = comment.user_profile || {};
    return profile.display_name || comment.entity_full_name || comment.entity_name || comment.user_name || '';
}

function errorText(value, fallbackKey) {
    var error = value && (value.error || (value.data && value.data.error));
    var message = typeof error === 'string' ? error : error && (error.content || error.message);
    return message ? AntLocalization.translateText(message) : T(fallbackKey);
}

function fileUrlOf(comment) {
    return comment.meddoc_full_path || comment.meddoc_short_path || '';
}

function isImage(comment) {
    return comment.meddoc_sub_type && String(comment.meddoc_sub_type).indexOf('image') === 0;
}

function extLabel(name) {
    var ext = name ? String(name).split('.').pop().toLowerCase() : '';
    if (ext === 'pdf') return 'PDF';
    if (ext === 'doc' || ext === 'docx') return 'DOC';
    if (ext === 'xls' || ext === 'xlsx') return 'XLS';
    return ext.slice(0, 4).toUpperCase();
}

function MentionText(props) {
    var text = props.text || '';
    var spans = storedMentionSpans(text, props.entities);
    if (!spans.length) return <div className="act-text">{text}</div>;
    var parts = [];
    var cursor = 0;
    spans.forEach(function (span) {
        if (span.start > cursor) parts.push(text.slice(cursor, span.start));
        parts.push(
            <span key={'m' + span.start} className="act-mention">
                @{span.label}
            </span>
        );
        cursor = span.end;
    });
    if (cursor < text.length) parts.push(text.slice(cursor));
    return <div className="act-text">{parts}</div>;
}

function EmojiPicker(props) {
    var ref = useRef(null);
    var onClose = props.onClose;
    useEffect(
        function () {
            function outside(e) {
                if (ref.current && !ref.current.contains(e.target)) onClose();
            }
            document.addEventListener('mousedown', outside);
            return function () {
                document.removeEventListener('mousedown', outside);
            };
        },
        [onClose]
    );
    return (
        <div className="act-emoji" ref={ref}>
            {EMOJI_CATEGORIES.map(function (category, index) {
                return (
                    <div key={index}>
                        <div className="act-emoji-label">{AntLocalization.translateText(category.label)}</div>
                        <div className="act-emoji-grid">
                            {category.emojis.map(function (emoji) {
                                return (
                                    <button key={emoji} type="button" className="act-emoji-item" onClick={function () { props.onSelect(emoji); }}>
                                        {emoji}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function FileCard(props) {
    var comment = props.comment;
    return (
        <button type="button" className="act-file" onClick={props.onOpen} title={comment.meddoc_name}>
            <span className="act-file-icon">
                <Icon name="file" size={18} stroke={1.5} />
                <span className="act-file-ext">{extLabel(comment.meddoc_name)}</span>
            </span>
            <span className="act-file-name">{comment.meddoc_name}</span>
        </button>
    );
}

export default function AntCommentThread(props) {
    var context = props.context;
    var voucherType = context && context.voucherType;
    var voucherCode = context && context.voucherCode;
    var voucherId = context && context.voucherId;
    var session = AntSession ? AntSession() : null;
    var api = useMemo(function () {
        return new AntAPI('user', 'ACUserComment');
    }, []);
    var userCode = AntAuth.getSessionInfo('user_code');
    var canComment = AntCondition.isVisible({}, { visible: { whenUserScreen: 'Comments' } });
    var canAttach = AntCondition.isVisible({}, { visible: { whenUserScreen: 'Attachments', whenSys: { use_Module_Attachment: 1 }, whenUser: { view_Module_Attachment: 1 } } });

    var [comments, setComments] = useState([]);
    var [page, setPage] = useState(0);
    var [hasMore, setHasMore] = useState(false);
    var [loading, setLoading] = useState(false);
    var [loadingMore, setLoadingMore] = useState(false);
    var [error, setError] = useState('');
    var [text, setText] = useState('');
    var [mentions, setMentions] = useState([]);
    var [caret, setCaret] = useState(0);
    var [suggestions, setSuggestions] = useState([]);
    var [activeSuggestion, setActiveSuggestion] = useState(0);
    var [dismissedQuery, setDismissedQuery] = useState(null);
    var [files, setFiles] = useState([]);
    var [reply, setReply] = useState(null);
    var [sending, setSending] = useState(false);
    var [showEmoji, setShowEmoji] = useState(false);
    var [viewer, setViewer] = useState(null);
    var [dragging, setDragging] = useState(false);

    var generation = useRef(0);
    var sendLock = useRef(false);
    var listRef = useRef(null);
    var inputRef = useRef(null);
    var fileRef = useRef(null);
    var atBottom = useRef(true);
    var suggestRevision = useRef(0);

    var scrollToBottom = function () {
        setTimeout(function () {
            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        }, 30);
    };

    // Newest first from the server (created_at: IDs are not chronological); shown oldest → newest.
    var load = useCallback(
        function (pageIndex, keepPosition) {
            if (!voucherType || !voucherCode) return;
            var request = ++generation.current;
            if (pageIndex === 0) setLoading(true);
            else setLoadingMore(true);
            setError('');
            var previousHeight = listRef.current ? listRef.current.scrollHeight : 0;
            api.post(
                '/' + api.routeURL + '/list',
                { getNew: true, isSearching: true, voucher_type: voucherType, voucher_id: voucherId, voucher_code: voucherCode, pageIndex: pageIndex, pageSize: PAGE_SIZE, orderBy: 'created_at', orderType: 'desc' },
                function (result) {
                    if (request !== generation.current) return;
                    setLoading(false);
                    setLoadingMore(false);
                    var rows = result && result.data && result.data.rows;
                    if (!result || result.error || !Array.isArray(rows)) {
                        setError(errorText(result, 'loadFailed'));
                        return;
                    }
                    var total = Number((result.data && (result.data.totalCount || result.data.totalRows)) || 0);
                    var chronological = rows.slice().reverse();
                    setComments(function (current) {
                        if (pageIndex === 0) return chronological;
                        var known = {};
                        current.forEach(function (row) {
                            known[String(row.id)] = true;
                        });
                        return chronological
                            .filter(function (row) {
                                return !known[String(row.id)];
                            })
                            .concat(current);
                    });
                    setPage(pageIndex);
                    setHasMore(total ? (pageIndex + 1) * PAGE_SIZE < total : rows.length === PAGE_SIZE);
                    if (pageIndex === 0 && !keepPosition) scrollToBottom();
                    if (pageIndex > 0)
                        setTimeout(function () {
                            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight - previousHeight;
                        }, 0);
                }
            );
        },
        [api, voucherType, voucherId, voucherCode]
    );

    useEffect(
        function () {
            setComments([]);
            setReply(null);
            setFiles([]);
            setText('');
            setMentions([]);
            load(0);
            return function () {
                generation.current += 1;
            };
        },
        [load]
    );

    useEffect(
        function () {
            if (props.autoFocus && inputRef.current)
                setTimeout(function () {
                    if (inputRef.current) inputRef.current.focus();
                }, 200);
        },
        [props.autoFocus]
    );

    // Live refresh when someone comments on this record.
    useEffect(
        function () {
            if (!session || !session.registerSocketListener || !voucherType) return undefined;
            var key = 'AntCommentThread-' + voucherType + '-' + (voucherCode || voucherId || '');
            session.registerSocketListener(function (data) {
                if (!data || data.event !== 'NewComment') return false;
                if (data.voucher_type !== voucherType) return false;
                if (data.voucher_id && voucherId && String(data.voucher_id) !== String(voucherId)) return false;
                if (data.voucher_code && voucherCode && String(data.voucher_code) !== String(voucherCode)) return false;
                load(0, !atBottom.current);
                return true;
            }, key);
            return function () {
                session.clearSocketListener(key);
            };
        },
        [session, voucherType, voucherId, voucherCode, load]
    );

    // ─── Mentions ────────────────────────────────────────────────
    var retainedMentions = useMemo(
        function () {
            return mentions.filter(function (item) {
                return mentionSpans(text, [item]).length > 0;
            });
        },
        [mentions, text]
    );
    var query = activeMentionQuery(text, caret, retainedMentions);

    useEffect(
        function () {
            var revision = ++suggestRevision.current;
            if (query === null || query === dismissedQuery || !voucherType || !voucherCode) {
                setSuggestions([]);
                return undefined;
            }
            var timer = setTimeout(function () {
                api.post('/' + api.routeURL + '/mentionUsers', { voucher_type: voucherType, voucher_code: voucherCode, query: query }, function (result) {
                    if (revision !== suggestRevision.current) return;
                    var rows = (result && result.data && result.data.rows) || [];
                    setSuggestions(
                        rows.filter(function (item) {
                            return (
                                item.display_name &&
                                !retainedMentions.some(function (selected) {
                                    return mentionKey(selected) === mentionKey(item);
                                })
                            );
                        })
                    );
                    setActiveSuggestion(0);
                });
            }, 250);
            return function () {
                clearTimeout(timer);
            };
        },
        [api, query, dismissedQuery, voucherType, voucherCode, retainedMentions]
    );

    var chooseMention = function (item) {
        var start = caret - query.length - 1;
        var token = '@' + mentionLabel(item) + ' ';
        var next = text.slice(0, start) + token + text.slice(caret);
        setText(next);
        setMentions(function (current) {
            return current
                .filter(function (existing) {
                    return mentionKey(existing) !== mentionKey(item);
                })
                .concat([item]);
        });
        setSuggestions([]);
        setDismissedQuery(null);
        var position = start + token.length;
        setCaret(position);
        setTimeout(function () {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.selectionStart = inputRef.current.selectionEnd = position;
            }
        }, 0);
    };

    var startMention = function () {
        var el = inputRef.current;
        var at = el ? el.selectionStart : text.length;
        var needsSpace = at > 0 && !/\s/.test(text[at - 1]);
        var insert = (needsSpace ? ' ' : '') + '@';
        var next = text.slice(0, at) + insert + text.slice(at);
        setText(next);
        setDismissedQuery(null);
        setCaret(at + insert.length);
        setTimeout(function () {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.selectionStart = inputRef.current.selectionEnd = at + insert.length;
            }
        }, 0);
    };

    // ─── Send ────────────────────────────────────────────────────
    var send = function () {
        var description = convertEmojiShortcuts(text.trim());
        if (sendLock.current || !canComment || (!description && !files.length) || !voucherType || !voucherCode) return;
        sendLock.current = true;
        setSending(true);
        setError('');
        var targets = retainedMentions.map(mentionTarget);
        var base = { voucher_type: voucherType, voucher_id: voucherId, voucher_code: voucherCode };
        var finish = function (failure) {
            sendLock.current = false;
            setSending(false);
            if (failure) {
                setError(failure);
                load(0, true);
                return;
            }
            setText('');
            setMentions([]);
            setReply(null);
            setFiles([]);
            atBottom.current = true;
            load(0);
        };
        if (files.length) {
            var queue = files.slice();
            var index = 0;
            var next = function (result) {
                if (result && (result.error || (result.data && result.data.error))) return finish(errorText(result, 'sendFailed'));
                if (index > 0) setFiles(queue.slice(index));
                if (index >= queue.length) return finish(null);
                var form = new FormData();
                form.append('files', queue[index]);
                form.append('voucher_type', voucherType);
                form.append('voucher_id', voucherId);
                form.append('voucher_code', voucherCode);
                form.append('description', index === 0 ? description : '');
                form.append('mentioned_targets', JSON.stringify(index === 0 ? targets : []));
                if (index === 0 && reply) form.append('reply_comment_id', reply.id);
                index += 1;
                api.upload('/' + api.routeURL + '/createWithAttachment', form, next);
            };
            next(null);
            return;
        }
        var payload = Object.assign({}, base, { description: description, mentioned_targets: targets });
        if (reply) payload.reply_comment_id = reply.id;
        api.post('/' + api.routeURL + '/create', payload, function (result) {
            finish(result && (result.error || (result.data && result.data.error)) ? errorText(result, 'sendFailed') : null);
        });
    };

    var onKeyDown = function (e) {
        if (e.nativeEvent && e.nativeEvent.isComposing) return;
        if (suggestions.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                var step = e.key === 'ArrowDown' ? 1 : -1;
                setActiveSuggestion(function (value) {
                    return (value + step + suggestions.length) % suggestions.length;
                });
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                chooseMention(suggestions[activeSuggestion] || suggestions[0]);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setDismissedQuery(query);
                setSuggestions([]);
                return;
            }
        }
        if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            send();
            return;
        }
        if (e.key === 'Escape' && reply) setReply(null);
    };

    // ─── Like (optimistic, like mobile) ─────────────────────────
    var toggleLike = function (comment) {
        var liked = likedCodesOf(comment).indexOf(userCode) >= 0;
        var apply = function (like) {
            setComments(function (current) {
                return current.map(function (row) {
                    if (String(row.id) !== String(comment.id)) return row;
                    var codes = likedCodesOf(row).filter(function (value) {
                        return value !== userCode;
                    });
                    if (like) codes.push(userCode);
                    return Object.assign({}, row, { liked_user_codes: codes, liked_count: codes.length });
                });
            });
        };
        apply(!liked);
        api.post('/' + api.routeURL + (liked ? '/unlikeComment' : '/likeComment'), { id: comment.id }, function (result) {
            if (!result || result.error) {
                apply(liked);
                setError(errorText(result, 'sendFailed'));
            }
        });
    };

    var addFiles = function (list) {
        var incoming = Array.from(list || []);
        if (incoming.length) setFiles(function (current) {
            return current.concat(incoming);
        });
    };

    var openViewer = function (target) {
        var all = [];
        var position = 0;
        comments.forEach(function (row) {
            if (row.meddoc_id && fileUrlOf(row)) {
                if (String(row.id) === String(target.id)) position = all.length;
                all.push({ name: row.meddoc_name || '', url: fileUrlOf(row), type: row.meddoc_sub_type || '' });
            }
        });
        if (all.length) setViewer({ files: all, index: position });
    };

    var insertEmoji = function (emoji) {
        var el = inputRef.current;
        var start = el ? el.selectionStart : text.length;
        var end = el ? el.selectionEnd : text.length;
        setText(text.slice(0, start) + emoji + text.slice(end));
        setShowEmoji(false);
        setTimeout(function () {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.selectionStart = inputRef.current.selectionEnd = start + emoji.length;
            }
        }, 0);
    };

    if (!voucherType || !voucherCode) {
        return (
            <div className="ant-comment-thread act-state">
                <span>{T('noVoucher')}</span>
            </div>
        );
    }

    // Consecutive file-only comments from the same person render as one gallery.
    var fileOnly = function (row) {
        return row && row.meddoc_id && row.meddoc_name && fileUrlOf(row) && !(row.description && row.description.trim()) && !row.reply_comment_id;
    };

    return (
        <div
            className={'ant-comment-thread' + (dragging ? ' act-dragging' : '')}
            onDragOver={function (e) {
                if (!canAttach || !canComment) return;
                e.preventDefault();
                setDragging(true);
            }}
            onDragLeave={function (e) {
                if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
            }}
            onDrop={function (e) {
                if (!canAttach || !canComment) return;
                e.preventDefault();
                setDragging(false);
                addFiles(e.dataTransfer && e.dataTransfer.files);
            }}
        >
            <div
                className="act-list"
                ref={listRef}
                onScroll={function () {
                    var el = listRef.current;
                    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
                }}
            >
                {hasMore && (
                    <button type="button" className="act-more" disabled={loadingMore} onClick={function () { load(page + 1); }}>
                        {loadingMore ? T('loading') : T('more')}
                    </button>
                )}
                {loading && !comments.length && <div className="act-state">{T('loading')}</div>}
                {!loading && !error && !comments.length && <div className="act-state">{T('empty')}</div>}
                {comments.map(function (comment, index) {
                    var previous = comments[index - 1];
                    var name = displayNameOf(comment);
                    if (fileOnly(comment) && fileOnly(previous) && displayNameOf(previous) === name) return null;
                    var gallery = [];
                    if (fileOnly(comment)) {
                        for (var i = index; i < comments.length && fileOnly(comments[i]) && displayNameOf(comments[i]) === name; i++) gallery.push(comments[i]);
                    }
                    var liked = likedCodesOf(comment).indexOf(userCode) >= 0;
                    var hasFile = comment.meddoc_id && comment.meddoc_name;
                    var fileGone = hasFile && !fileUrlOf(comment);
                    var images = gallery.filter(isImage);
                    var documents = gallery.filter(function (row) {
                        return !isImage(row);
                    });
                    return (
                        <div key={comment.id} className="act-item">
                            <Avatar src={comment.avatar_full_path} name={name} />
                            <div className="act-body">
                                <div className="act-meta">
                                    <span className="act-author">{name}</span>
                                    <span className="act-time">{sidebarRelativeTime(comment.created_at)}</span>
                                </div>
                                {comment.reply_comment_id && comment.reply_user_name && (
                                    <div className="act-quote">
                                        <span className="act-quote-author">{comment.reply_user_name}</span>
                                        <span className="act-quote-text">{comment.reply_description || comment.reply_meddoc_name || ''}</span>
                                    </div>
                                )}
                                {comment.description && <MentionText text={comment.description} entities={comment.mention_entities} />}
                                {canAttach && hasFile && fileGone && <div className="act-file-gone">{comment.meddoc_name} · {T('deleted')}</div>}
                                {canAttach && gallery.length > 1 && (
                                    <div className="act-attachments">
                                        {images.length > 0 && (
                                            <div className="act-grid">
                                                {images.map(function (row) {
                                                    return <img key={row.id} src={fileUrlOf(row)} alt={row.meddoc_name || ''} onClick={function () { openViewer(row); }} />;
                                                })}
                                            </div>
                                        )}
                                        {documents.map(function (row) {
                                            return <FileCard key={row.id} comment={row} onOpen={function () { openViewer(row); }} />;
                                        })}
                                    </div>
                                )}
                                {canAttach && hasFile && !fileGone && gallery.length <= 1 && (
                                    <div className="act-attachments">
                                        {isImage(comment) ? (
                                            <img className="act-image" src={fileUrlOf(comment)} alt={comment.meddoc_name || ''} onClick={function () { openViewer(comment); }} />
                                        ) : (
                                            <FileCard comment={comment} onOpen={function () { openViewer(comment); }} />
                                        )}
                                    </div>
                                )}
                                <div className="act-actions">
                                    {canComment && (
                                        <button type="button" className="act-action" onClick={function () {
                                            setReply({ id: comment.id, user_name: name });
                                            if (inputRef.current) inputRef.current.focus();
                                        }}>
                                            <Icon name="reply" size={14} />
                                            <span>{T('reply')}</span>
                                        </button>
                                    )}
                                    <button type="button" className={'act-action act-like' + (liked ? ' liked' : '')} aria-pressed={liked} title={T('like')} onClick={function () { toggleLike(comment); }}>
                                        <Icon name="heart" size={14} fill={liked ? 'currentColor' : 'none'} />
                                        {Number(comment.liked_count || 0) > 0 && <span>{comment.liked_count}</span>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {error && (
                <div className="act-error" role="alert">
                    <span>{error}</span>
                    <button type="button" onClick={function () { setError(''); load(0, true); }}>
                        {T('retry')}
                    </button>
                </div>
            )}

            {canComment ? (
                <div className="act-composer">
                    {reply && (
                        <div className="act-chip-row">
                            <span className="act-reply-chip">
                                <Icon name="reply" size={13} />
                                <span>
                                    {T('replyTo')} <strong>{reply.user_name}</strong>
                                </span>
                                <button type="button" aria-label={T('remove')} onClick={function () { setReply(null); }}>
                                    <Icon name="close" size={12} />
                                </button>
                            </span>
                        </div>
                    )}
                    {files.length > 0 && (
                        <div className="act-chip-row">
                            {files.map(function (file, index) {
                                return (
                                    <span key={index + file.name} className="act-file-chip">
                                        <Icon name="clip" size={13} />
                                        <span className="act-file-chip-name">{file.name}</span>
                                        <button type="button" aria-label={T('remove')} disabled={sending} onClick={function () {
                                            setFiles(function (current) {
                                                return current.filter(function (_, position) {
                                                    return position !== index;
                                                });
                                            });
                                        }}>
                                            <Icon name="close" size={12} />
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <div className="act-input-box">
                        {suggestions.length > 0 && (
                            <div className="act-suggestions" role="listbox">
                                {suggestions.map(function (item, index) {
                                    return (
                                        <button
                                            key={mentionKey(item)}
                                            type="button"
                                            role="option"
                                            aria-selected={index === activeSuggestion}
                                            className={'act-suggestion' + (index === activeSuggestion ? ' active' : '')}
                                            onMouseDown={function (e) {
                                                e.preventDefault();
                                                chooseMention(item);
                                            }}
                                            onMouseEnter={function () { setActiveSuggestion(index); }}
                                        >
                                            <Avatar src={item.avatar_full_path} name={item.display_name} />
                                            <span className="act-suggestion-text">
                                                <span className="act-suggestion-name">{item.display_name}</span>
                                                {item.detail && <span className="act-suggestion-detail">{item.detail}</span>}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <textarea
                            ref={inputRef}
                            className="act-input"
                            value={text}
                            rows={2}
                            placeholder={T('placeholder')}
                            onChange={function (e) {
                                setText(e.target.value);
                                setCaret(e.target.selectionStart);
                                setDismissedQuery(null);
                            }}
                            onSelect={function (e) { setCaret(e.target.selectionStart); }}
                            onKeyDown={onKeyDown}
                            onBlur={function () { setTimeout(function () { setSuggestions([]); }, 150); }}
                        />
                        <div className="act-toolbar">
                            {canAttach && (
                                <button type="button" className="act-tool" title={T('attach')} aria-label={T('attach')} disabled={sending} onClick={function () { if (fileRef.current) fileRef.current.click(); }}>
                                    <Icon name="clip" />
                                </button>
                            )}
                            <button type="button" className="act-tool" title={T('mention')} aria-label={T('mention')} onClick={startMention}>
                                <Icon name="at" />
                            </button>
                            <button type="button" className={'act-tool' + (showEmoji ? ' active' : '')} title={T('emoji')} aria-label={T('emoji')} onClick={function () { setShowEmoji(!showEmoji); }}>
                                <Icon name="smile" />
                            </button>
                            <span className="act-spacer" />
                            <button type="button" className="act-send" title={T('send')} aria-label={T('send')} disabled={sending || (!text.trim() && !files.length)} onClick={send}>
                                <Icon name="send" />
                            </button>
                        </div>
                        {showEmoji && <EmojiPicker onSelect={insertEmoji} onClose={function () { setShowEmoji(false); }} />}
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        className="act-hidden-input"
                        onChange={function (e) {
                            addFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                </div>
            ) : (
                <div className="act-readonly">{T('readOnly')}</div>
            )}
            {viewer && <AntFileViewer visible files={viewer.files} initialIndex={viewer.index} onClose={function () { setViewer(null); }} />}
        </div>
    );
}
